import type { AdminAddonGroup, AdminAddonOption, LocalizedPair } from '@diora/contracts';
import { isFree } from '@diora/contracts';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/app-shell';
import { BilingualField } from '@/components/bilingual';
import { FormLoading, FormPage, RecordPage, useDirty, useRecord } from '@/components/form-page';
import { Money } from '@/components/money';
import { PublishBlockers, RowActions, StatusBadge, TranslationBadge } from '@/components/records';
import { Alert, DataTable, EmptyState, Field, Input, SkeletonRows } from '@/components/ui';
import { apiGet, apiPatch, apiPost, useAdminMutation, useLifecycle } from '@/hooks/admin';
import { qk } from '@/lib/query';
import { useAuth } from '@/providers/auth';
import { useLocale } from '@/providers/locale';
import { ADDONS_LIST, GROUPS_URL } from './addons';

export const OPTIONS_URL = '/admin/addon-options';
const OPTIONS_INVALIDATE = [['addon-options'], ['addon-option'], ['addon-groups'], ['addon-group']];

/** Where an option's own form returns to: the group it belongs to. */
const optionsList = (groupId: string) => `${ADDONS_LIST}/${groupId}/options`;

/**
 * The options inside one add-on group.
 *
 * This was a modal that opened a second modal — a list of options over the
 * group list, with an option editor stacked on top of both. Two stacked
 * dialogs is the point at which a modal system has stopped working: there is
 * no back, the Escape key is ambiguous, and neither layer can be linked to.
 */
export function AddonOptionsPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { groupId = '' } = useParams();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const group = useRecord<AdminAddonGroup>(qk.addonGroup(groupId), GROUPS_URL, groupId);

  const query = useQuery({
    queryKey: qk.addonOptions(groupId),
    queryFn: () =>
      apiGet<AdminAddonOption[]>(OPTIONS_URL, { page: 1, pageSize: 100, groupId }),
    enabled: Boolean(groupId),
  });

  const lifecycle = useLifecycle(OPTIONS_URL, OPTIONS_INVALIDATE);

  // The API's list filter is honoured server-side, but this screen is named
  // after one group and must never show another's rows.
  const rows = (query.data?.data ?? []).filter((option) => option.groupId === groupId);
  const name = group.row
    ? group.row.name[locale] || group.row.name.ar || group.row.name.en
    : '';

  return (
    <>
      <PageHeader
        width="narrow"
        title={name ? `${t('addons.options')} — ${name}` : t('addons.options')}
        description={t('addons.optionPriceHint')}
        actions={
          <>
            {canEdit && (
              <Link
                to={`${optionsList(groupId)}/new`}
                className="dio-btn dio-btn--primary dio-btn--sm"
              >
                <Plus className="dio-icon" aria-hidden="true" />
                {t('addons.newOption')}
              </Link>
            )}
            <Link to={ADDONS_LIST} className="dio-btn dio-btn--ghost dio-btn--sm">
              {t('common.back')}
            </Link>
          </>
        }
      />

      {query.isLoading ? (
        <SkeletonRows rows={3} />
      ) : rows.length === 0 ? (
        <div className="flex flex-col gap-3">
          <EmptyState title={t('addons.emptyOptions')} />
          <Alert tone="info">{t('addons.emptyGroupPublishable')}</Alert>
        </div>
      ) : (
        <DataTable
          caption={t('addons.options')}
          columns={[
            t('addons.optionName'),
            t('addons.optionPrice'),
            t('common.status'),
            <span className="sr-only">{t('common.actions')}</span>,
          ]}
        >
          {rows.map((option) => (
            <tr key={option.id}>
              <td>
                <div className="dio-table__name flex flex-wrap items-center gap-2">
                  <span lang="ar" dir="rtl">
                    {option.name.ar || '—'}
                  </span>
                  <span className="dio-meta" aria-hidden="true">
                    /
                  </span>
                  <span lang="en" dir="ltr">
                    {option.name.en || '—'}
                  </span>
                </div>
                <TranslationBadge translation={option.translation} />
              </td>

              <td className="dio-table__num">
                {/* null and "0.00" say different things, and both are shown as
                    themselves rather than collapsed into one. */}
                {option.price === null ? (
                  t('addons.unpricedOption')
                ) : isFree(option.price) ? (
                  t('addons.freeOption')
                ) : (
                  <Money amount={option.price} />
                )}
              </td>

              <td>
                <StatusBadge status={option.publicationStatus} />
              </td>

              <td className="dio-table__actions">
                <div className="dio-actions">
                  <Link
                    to={`${optionsList(groupId)}/${option.id}`}
                    className="dio-btn dio-btn--secondary dio-btn--sm"
                  >
                    {canEdit ? t('common.edit') : t('common.details')}
                  </Link>
                  <RowActions
                    status={option.publicationStatus}
                    translation={option.translation}
                    canEdit={canEdit}
                    busy={lifecycle.busy}
                    label={t('common.openMenuFor', { name: option.name.ar || option.name.en })}
                    handlers={{
                      onPublish: () =>
                        lifecycle.publish.mutate({ id: option.id, version: option.version }),
                      onUnpublish: () =>
                        lifecycle.unpublish.mutate({ id: option.id, version: option.version }),
                      onArchive: () =>
                        lifecycle.archive.mutate({ id: option.id, version: option.version }),
                      onRestore: () =>
                        lifecycle.restore.mutate({ id: option.id, version: option.version }),
                    }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </>
  );
}

export function AddonOptionFormPage() {
  const { t } = useTranslation();
  const { groupId = '', optionId } = useParams();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const record = useRecord<AdminAddonOption>(qk.addonOption(optionId ?? ''), OPTIONS_URL, optionId);

  if (!groupId) return <FormLoading title={t('addons.newOption')} backTo={ADDONS_LIST} />;
  if (!optionId) return <AddonOptionForm groupId={groupId} option={null} canEdit={canEdit} />;

  return (
    <RecordPage title={t('addons.editOption')} backTo={optionsList(groupId)} record={record}>
      {(row) => <AddonOptionForm key={row.id} groupId={groupId} option={row} canEdit={canEdit} />}
    </RecordPage>
  );
}

function AddonOptionForm({
  groupId,
  option,
  canEdit,
}: {
  groupId: string;
  option: AdminAddonOption | null;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [name, setName] = useState<LocalizedPair>(option?.name ?? { ar: '', en: '' });
  const [price, setPrice] = useState(option?.price ?? '');

  const dirty = useDirty({ name, price });
  const backTo = optionsList(groupId);

  const save = useAdminMutation(
    () => {
      const body = {
        groupId,
        nameAr: name.ar,
        nameEn: name.en,
        // Empty means "not priced yet" and blocks publishing; "0" means free.
        price: price.trim() === '' ? undefined : price.trim(),
      };
      return option
        ? apiPatch(`${OPTIONS_URL}/${option.id}`, { ...body, expectedVersion: option.version })
        : apiPost(OPTIONS_URL, body);
    },
    {
      successMessage: option ? t('common.saved') : t('common.created'),
      invalidate: OPTIONS_INVALIDATE,
      onSuccess: () => navigate(backTo),
    },
  );

  return (
    <FormPage
      title={option ? t('addons.editOption') : t('addons.newOption')}
      backTo={backTo}
      dirty={dirty}
      saving={save.isPending}
      canSave={name.ar.trim().length > 0}
      onSave={canEdit ? () => save.mutate(undefined) : undefined}
    >
      {option && <PublishBlockers translation={option.translation} />}

      <BilingualField
        label={t('addons.optionName')}
        value={name}
        onChange={setName}
        required
        maxLength={100}
        disabled={!canEdit}
      />
      <Field label={t('addons.optionPrice')} htmlFor="option-price" hint={t('addons.optionPriceHint')}>
        <Input
          id="option-price"
          type="text"
          inputMode="decimal"
          dir="ltr"
          className="tabular"
          disabled={!canEdit}
          value={price}
          placeholder="0.00"
          onChange={(event) => setPrice(event.target.value)}
        />
      </Field>
    </FormPage>
  );
}
